import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AREA_KIND_LABELS, type Role, type Tenant, type User } from '@ww/shared';
import { api, type UserInput } from '../api';
import TenantWizard from '../components/TenantWizard';
import { ErrorBox, PageHeader, PasswordField, Toggle } from '../components/ui';
import { useLive } from '../hooks/useLive';
import { ROLE_LABELS, errorMessage, isAdmin } from '../lib/status';
import { fmtDateTime } from '../lib/time';

const EMPTY_USER = { name: '', username: '', email: '', role: 'viewer' as Role, password: '' };

function areaSummary(t: Tenant): string {
  if (t.area) return t.area.label;
  // Legacy tenants predate the area field but were seeded with a county boundary.
  return t.boundary ? `${t.countyName} (county)` : '—';
}

function TenantsSection({ tenants, onChanged }: { tenants: Tenant[] | null; onChanged: () => Promise<void> }) {
  const navigate = useNavigate();
  const { setActingTenantId } = useLive();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleStatus = async (t: Tenant) => {
    try {
      await api.updateTenant(t._id, { status: t.status === 'active' ? 'disabled' : 'active' });
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="admin-grid">
      <section className="panel" style={{ overflow: 'hidden' }}>
        <div className="panel-head">
          <span className="lbl" style={{ flexGrow: 1 }}>
            Tenants
          </span>
          <span className="mono muted" style={{ fontSize: 12.5 }}>
            {tenants?.length ?? ''}
          </span>
          {!creating && (
            <button type="button" className="btn btn-primary btn-sm hov" onClick={() => setCreating(true)}>
              New tenant
            </button>
          )}
        </div>
        <ErrorBox error={error} />
        <div className="scroll" style={{ maxHeight: 460 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Coverage area</th>
                <th>Zones</th>
                <th>Timezone</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {!tenants && (
                <tr>
                  <td colSpan={6} className="muted">
                    Loading…
                  </td>
                </tr>
              )}
              {(tenants ?? []).map((t) => (
                <tr key={t._id}>
                  <td>
                    <button
                      type="button"
                      className="btn-link hov"
                      style={{ fontWeight: 600, fontSize: 14 }}
                      onClick={() => navigate(`/admin/tenants/${t._id}`)}
                    >
                      {t.name}
                    </button>
                    <div className="muted mono" style={{ fontSize: 12.5 }}>
                      {t.slug} · {t.state}
                    </div>
                  </td>
                  <td>
                    <span className={`chip area-chip area-${t.area?.kind ?? 'county'}`}>
                      {t.area ? AREA_KIND_LABELS[t.area.kind] : 'County'}
                    </span>
                    <div className="muted" style={{ fontSize: 12.5 }}>{areaSummary(t)}</div>
                  </td>
                  <td className="mono" style={{ fontSize: 12.5 }}>
                    {(t.zoneCodes ?? []).length ? (
                      t.zoneCodes.join(', ')
                    ) : (
                      // Empty zone codes silently drop every alert issued without a polygon.
                      <span className="tag tag-stale" title="Watches and advisories will not alert">
                        no zones
                      </span>
                    )}
                  </td>
                  <td className="mono" style={{ fontSize: 12.5 }}>
                    {t.timezone}
                  </td>
                  <td>
                    <Toggle on={t.status === 'active'} label={`Tenant ${t.name} active`} onChange={() => void toggleStatus(t)} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm hov"
                      title={`Open ${t.name}'s portal to help them find their way around`}
                      onClick={() => {
                        setActingTenantId(t._id);
                        navigate('/');
                      }}
                    >
                      Drop in
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {creating ? (
        <TenantWizard
          onCancel={() => setCreating(false)}
          onCreated={async (tenant) => {
            setCreating(false);
            await onChanged();
            // Act as the new tenant straight away, then open its panel.
            setActingTenantId(tenant._id);
            navigate(`/admin/tenants/${tenant._id}`, { state: { justCreated: true } });
          }}
        />
      ) : (
        <section className="panel" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginTop: 0 }}>Onboard a customer</h2>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
            Pick the state, then the county, city or custom area they have overwatch of. The name,
            slug, map centre, timezone and NWS zone codes are all derived from that choice and stay
            editable before you create the tenant.
          </p>
          <button type="button" className="btn btn-primary hov" onClick={() => setCreating(true)}>
            New tenant
          </button>
        </section>
      )}
    </div>
  );
}

function UsersSection({
  isSuper,
  tenants,
  ownTenantId,
  currentUserId,
}: {
  isSuper: boolean;
  tenants: Tenant[] | null;
  ownTenantId: string | null;
  currentUserId: string;
}) {
  const [tenantId, setTenantId] = useState<string>(ownTenantId ?? '');
  const [users, setUsers] = useState<User[] | null>(null);
  const [f, setF] = useState(EMPTY_USER);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const set = (patch: Partial<typeof EMPTY_USER>) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    if (isSuper && !tenantId && tenants && tenants.length) setTenantId(tenants[0]!._id);
  }, [isSuper, tenantId, tenants]);

  const load = useCallback(async () => {
    try {
      const list = await api.users(isSuper ? tenantId || null : null);
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(errorMessage(err));
      setUsers([]);
    }
  }, [isSuper, tenantId]);

  useEffect(() => {
    setUsers(null);
    void load();
  }, [load]);

  const visible = (users ?? []).filter((u) => !isSuper || !tenantId || u.tenantId === tenantId || u.tenantId === null);
  const roles: Role[] = isSuper
    ? ['viewer', 'dispatcher', 'tenant_admin', 'super_admin']
    : ['viewer', 'dispatcher', 'tenant_admin'];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!f.name.trim() || !f.username.trim() || !f.password) return setError('Name, username and password are required.');
    if (f.role !== 'super_admin' && isSuper && !tenantId) return setError('Select a tenant first.');
    const body: UserInput = {
      name: f.name.trim(),
      username: f.username.trim(),
      email: f.email.trim(),
      role: f.role,
      password: f.password,
      tenantId: f.role === 'super_admin' ? null : isSuper ? tenantId : ownTenantId,
    };
    setBusy(true);
    setError(null);
    try {
      await api.createUser(body);
      setNotice(`User “${body.username}” created.`);
      setF(EMPTY_USER);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (u: User) => {
    try {
      await api.updateUser(u._id, { status: u.status === 'active' ? 'disabled' : 'active' });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const tenantName = tenants?.find((t) => t._id === tenantId)?.name;

  return (
    <div className="admin-grid">
      <section className="panel" style={{ overflow: 'hidden' }}>
        <div className="panel-head" style={{ flexWrap: 'wrap' }}>
          <span className="lbl" style={{ flexGrow: 1 }}>
            Users{!isSuper ? ' in your tenant' : ''}
          </span>
          {isSuper && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="lbl">Tenant</span>
              <select className="fld" style={{ width: 240 }} value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                {(tenants ?? []).map((t) => (
                  <option key={t._id} value={t._id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="scroll" style={{ maxHeight: 520 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Username</th>
                <th>Role</th>
                <th>Updated</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {!users && (
                <tr>
                  <td colSpan={5} className="muted">
                    Loading…
                  </td>
                </tr>
              )}
              {users && visible.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted" style={{ textAlign: 'center', padding: 24 }}>
                    No users.
                  </td>
                </tr>
              )}
              {visible.map((u) => {
                const self = u._id === currentUserId;
                const locked = self || (!isSuper && u.role === 'super_admin');
                return (
                  <tr key={u._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {u.name}
                        {self ? <span className="muted"> (you)</span> : null}
                      </div>
                      <div className="muted">{u.email || '—'}</div>
                    </td>
                    <td className="mono">{u.username}</td>
                    <td>{ROLE_LABELS[u.role] ?? u.role}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {fmtDateTime(u.updatedAt)}
                    </td>
                    <td>
                      <Toggle
                        on={u.status === 'active'}
                        label={`User ${u.username} active`}
                        onChange={locked ? undefined : () => void toggle(u)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <form className="panel" onSubmit={submit} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Create user</h2>
        <div className="muted" style={{ fontSize: 13.5 }}>
          {f.role === 'super_admin'
            ? 'Super admins are not bound to a tenant.'
            : `Tenant: ${isSuper ? tenantName ?? 'select a tenant' : 'your tenant'}`}
        </div>
        <ErrorBox error={error} />
        {notice && <div className="ok-box">{notice}</div>}
        <label className="field">
          <span className="lbl">Name</span>
          <input className="fld" value={f.name} onChange={(e) => set({ name: e.target.value })} />
        </label>
        <div className="grid-2">
          <label className="field">
            <span className="lbl">Username</span>
            <input className="fld mono" autoComplete="off" value={f.username} onChange={(e) => set({ username: e.target.value })} />
          </label>
          <label className="field">
            <span className="lbl">Role</span>
            <select className="fld" value={f.role} onChange={(e) => set({ role: e.target.value as Role })}>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="lbl">Email</span>
          <input className="fld" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} />
        </label>
        <PasswordField
          label="Initial password"
          autoComplete="new-password"
          value={f.password}
          onChange={(v) => set({ password: v })}
        />
        <button type="submit" className="btn btn-primary hov" disabled={busy} style={{ alignSelf: 'flex-end' }}>
          {busy ? 'Creating…' : 'Create user'}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  // One shared tenant list: the header switcher reads the same state, so a newly
  // created tenant shows up in both places at once.
  const { me, tenants, refreshTenants } = useLive();
  const role = me?.user.role;
  const isSuper = role === 'super_admin';
  const [tab, setTab] = useState<'tenants' | 'users'>(isSuper ? 'tenants' : 'users');

  // Allow-list — see RequireAdmin in App.tsx.
  if (!me || !isAdmin(role)) return <Navigate to="/" replace />;

  return (
    <div className="page page-scroll" style={{ gap: 14 }}>
      <PageHeader
        title="Administration"
        sub={isSuper ? 'Manage tenants and their users' : `Manage users for ${me.tenant?.name ?? 'your tenant'}`}
      />
      {isSuper && (
        <div role="tablist" className="tabs" style={{ flexShrink: 0 }}>
          <button type="button" role="tab" className="tab" aria-selected={tab === 'tenants'} onClick={() => setTab('tenants')}>
            Tenants
          </button>
          <button type="button" role="tab" className="tab" aria-selected={tab === 'users'} onClick={() => setTab('users')}>
            Users
          </button>
        </div>
      )}
      {isSuper && tab === 'tenants' && <TenantsSection tenants={tenants} onChanged={refreshTenants} />}
      {(tab === 'users' || !isSuper) && (
        <UsersSection
          isSuper={isSuper}
          tenants={tenants}
          ownTenantId={me.user.tenantId ?? me.tenant?._id ?? null}
          currentUserId={me.user._id}
        />
      )}
    </div>
  );
}
